import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/useAuth';
import { NOTIFICATION_ENDPOINTS, fetchWithAuth } from '../config/api';
import { notificationRoute } from '../utils/notificationRoute';

export interface AppNotification {
    id: string;
    title: string;
    message?: string;
    isRead: boolean;
    createdAt: string;
    /** e.g. 'enquiry', 'development_application'. */
    notificationType?: string;
    /**
     * An in-app route the server wants the click to open (e.g. '/enquiries?enquiry=…'). Takes
     * precedence over the DA routing below; only ever honoured when it is a same-origin path.
     */
    link?: string;
    // New server-provided fields for robust routing.
    applicationId?: string;
    councilSlug?: string; // e.g., 'south-australia', 'gold-coast'
    // Backward-compatibility field.
    addressId?: string;
    // Not part of this hook's documented contract, but the API payload carries it and the legacy
    // NotificationIcon renders from it (thumbnail + street/suburb subtitle). Kept optional here
    // so that consumer's rendering stays byte-for-byte identical after the extraction.
    addressSummary?: {
        street: string;
        suburb: string;
        state: string;
        postcode: string;
        propertyType: string;
        price?: number;
        imageUrl?: string;
    };
}

declare global {
    interface Window {
        /**
         * Overrides the unread-count poll interval, in ms. Set by the full-stack E2E suite (through
         * an init script) so a spec can see a new notification without waiting 30 seconds; never
         * set in production.
         */
        __DA_NOTIFICATION_POLL_MS__?: number;
    }
}

/** How often the bell re-checks for new notifications while the tab is visible. */
export const DEFAULT_NOTIFICATION_POLL_MS = 30_000;

/** The floor for the override: a stray tiny value must not turn the bell into a request loop. */
export const MIN_NOTIFICATION_POLL_MS = 1_000;

/**
 * Event-driven refetches (focus, tab shown, popover opened) are skipped while a fetch is in flight
 * or one started less than this long ago — focus and visibilitychange routinely fire together.
 * The poll's own tick is exempt, so an override shorter than this (the demo's 1.5s) still fires.
 */
export const NOTIFICATION_REFETCH_DEDUPE_MS = 2_000;

function pollIntervalMs(): number {
    const override = typeof window !== 'undefined' ? window.__DA_NOTIFICATION_POLL_MS__ : undefined;
    return typeof override === 'number' && Number.isFinite(override) && override > 0
        ? Math.max(MIN_NOTIFICATION_POLL_MS, override)
        : DEFAULT_NOTIFICATION_POLL_MS;
}

export interface UseNotificationsResult {
    notifications: AppNotification[];
    unreadCount: number;
    refresh: () => Promise<void>;
    markAllRead: () => Promise<void>;
    /** Marks read, removes locally, and returns the route to navigate to. */
    openNotification: (notification: AppNotification) => Promise<string>;
}

/**
 * Data layer for the notification bell, shared by the legacy header's `NotificationIcon` and the
 * redesign's `NotificationsPopover` so there is one source of truth rather than two copies that
 * drift. Extracted verbatim from `NotificationIcon.tsx` (which had no tests and ran unmodified in
 * production) — every behaviour below is preserved intentionally, not reconsidered:
 *
 * - polls every 30 seconds (`window.__DA_NOTIFICATION_POLL_MS__` overrides it, floored at 1s, for
 *   E2E and the demo) while a user is present AND the tab is visible; refetches when the tab is
 *   shown again, the window regains focus, or `refresh()` is called (the popover opening) — those
 *   three deduped: skipped while a fetch is in flight or within 2s of the last one started
 * - listens for the window `notifications:updated` event and refetches on it
 * - suppresses (via `hiddenIds`) notifications already clicked this session, so a slow refetch
 *   racing a click can't resurrect an item the user just dismissed
 * - caps the list at the 10 most recent unread items
 * - a click removes the item locally first (optimistic), then marks it read, then refetches the
 *   list, then refetches the count separately
 * - routes by the server's `link` (in-app paths only), then councilSlug+applicationId, then
 *   addressId, then falls back to /notifications
 *
 * Outside-click / open-close handling is presentation, not data, so it stays in each consumer.
 */
export function useNotifications(): UseNotificationsResult {
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
    const [unreadCount, setUnreadCount] = useState(0);
    const { user } = useAuth();

    const fetchNotifications = useCallback(async () => {
        if (!user) return;

        // Errors here are swallowed silently — pre-existing behaviour carried over from
        // NotificationIcon.tsx, not a choice made in this extraction. A failed poll should not
        // surface an error to the user; it just tries again on the next interval/event. Left
        // as-is per the brief: fixing this is out of scope for a behaviour-preserving refactor.
        try {
            // fetchWithAuth, not a bare fetch. A relative '/api/...' resolves against the DEV
            // SERVER's own origin (localhost:5173) and so depends on Vite's proxy, whose target is
            // a hard-coded http://localhost:5183 — wrong the moment the API is run on any other
            // port or over https, which is how these calls 404'd while the rest of the app worked.
            // fetchWithAuth resolves the same path against the configured API origin, and carries
            // the bearer token and the X-Business-Id header these calls were also missing.
            const count = await fetchWithAuth<number>(NOTIFICATION_ENDPOINTS.NOTIFICATION_COUNT, {
                method: 'GET',
            });
            setUnreadCount(count);

            // Only unread, limited to 10 most recent.
            {
                const data = await fetchWithAuth<AppNotification[]>(
                    `${NOTIFICATION_ENDPOINTS.NOTIFICATIONS}?unreadOnly=true&limit=10`,
                    { method: 'GET', cache: 'no-store' },
                );
                // Ensure we only keep unread items and limit to 10
                const unread = Array.isArray(data) ? data.filter((n: AppNotification) => !n.isRead) : [];
                // Suppress any IDs the user already clicked in this session
                const filtered = unread.filter((n: AppNotification) => !hiddenIds.has(n.id));
                setNotifications(filtered.slice(0, 10));
            }
        } catch {
            // Silently handle fetch errors
        }
    }, [user, hiddenIds]);

    // Bookkeeping for the dedupe: how many fetches are in flight, and when the last one started.
    const inFlight = useRef(0);
    const lastStartedAt = useRef(0);
    const trackedFetch = useCallback(async () => {
        inFlight.current += 1;
        lastStartedAt.current = Date.now();
        try {
            await fetchNotifications();
        } finally {
            inFlight.current -= 1;
        }
    }, [fetchNotifications]);

    // The latest fetch, for the timers and listeners below. Read through a ref so that a change to
    // hiddenIds (which re-creates fetchNotifications) does not tear down and restart the interval.
    const fetchRef = useRef(trackedFetch);
    useEffect(() => {
        fetchRef.current = trackedFetch;
    }, [trackedFetch]);

    /** For event-driven triggers: skipped while a fetch is in flight or when one has only just run. */
    const refreshIfStale = useCallback(async () => {
        if (inFlight.current > 0) return;
        if (Date.now() - lastStartedAt.current < NOTIFICATION_REFETCH_DEDUPE_MS) return;
        await fetchRef.current();
    }, []);

    // Fetch on mount and whenever the user or the hidden set changes (the latter so a mark-all
    // re-syncs at once).
    useEffect(() => {
        if (user) void trackedFetch();
    }, [user, trackedFetch]);

    // Poll while the tab is visible; catch up when it becomes visible again or the window regains
    // focus. A hidden tab skips its ticks rather than hammering the API for a badge nobody sees.
    useEffect(() => {
        if (!user) return;

        const isVisible = () => document.visibilityState !== 'hidden';
        const interval = setInterval(() => {
            if (isVisible()) void fetchRef.current();
        }, pollIntervalMs());
        const onVisibilityChange = () => {
            if (isVisible()) void refreshIfStale();
        };
        const onFocus = () => void refreshIfStale();

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('focus', onFocus);
        };
    }, [user, refreshIfStale]);

    // Listen for global notifications updates (e.g., from the Notifications page).
    useEffect(() => {
        const handleGlobalUpdate = () => {
            if (user) {
                void trackedFetch();
            }
        };
        window.addEventListener('notifications:updated', handleGlobalUpdate);
        return () => {
            window.removeEventListener('notifications:updated', handleGlobalUpdate);
        };
    }, [trackedFetch, user]);

    const openNotification = useCallback(async (notification: AppNotification): Promise<string> => {
        // Always remove from local list first so it disappears immediately
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        setHiddenIds(prev => new Set(prev).add(notification.id));
        // Mark as read if not already read
        if (!notification.isRead) {
            try {
                await fetchWithAuth(NOTIFICATION_ENDPOINTS.NOTIFICATION_READ(notification.id), {
                    method: 'PUT',
                });
                // Update unread count
                setUnreadCount(prev => Math.max(0, prev - 1));
                // Refresh from server to ensure consistency (both list and count)
                await trackedFetch();
                try {
                    setUnreadCount(await fetchWithAuth<number>(
                        NOTIFICATION_ENDPOINTS.NOTIFICATION_COUNT, { method: 'GET', cache: 'no-store' }));
                } catch { /* count fetch failed, ignore */ }
            } catch {
                // Silently handle mark-as-read errors
            }
        }

        // The shared routing: a safe server link (enquiries open their inbox row), then the DA, then
        // the address; the notifications page when it names nowhere.
        return notificationRoute(notification) ?? '/notifications';
    }, [trackedFetch]);

    const markAllRead = useCallback(async () => {
        try {
            await fetchWithAuth(NOTIFICATION_ENDPOINTS.MARK_ALL_READ, { method: 'PUT' });

            // Clear local unread notifications (hide from bell)
            setNotifications([]);
            setHiddenIds(new Set());
            // Reset unread count
            setUnreadCount(0);
        } catch {
            // Silently handle mark-all-as-read errors
        }
    }, []);

    return {
        notifications,
        unreadCount,
        refresh: refreshIfStale,
        markAllRead,
        openNotification,
    };
}
